import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleInput,
  BubbleOverlay,
  BubbleSelect,
} from '@go-client/components/BubbleUI';
import { BubbleImageThumbnailPurpose } from '@go-client/components/BubbleUI/BubbleImageThumbnail';
import { Bytes } from '@go-client/components/Image/Bytes';
import { UploadButton } from '@go-client/components/UIkit/forms/UploadButton/UploadButton';
import { getImageRequirementOptions, ORIGINAL_OPTIMIZATION } from '@go-shared/lib/image';
import { ImageRequirementOption } from '@go-shared/types/image';
import { Alert, Popup } from '@spark-labs/avocado-ui';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { useServerInfo } from './ServerInfoContext';
import UPNG from 'upng-js';

export type ImageCropperAllowedMimes = 'image/jpeg' | 'image/png' | 'image/webp';

interface ImageCropperSpecs {
  key: string;
  realWidth?: number;
  realHeight?: number;
}

export interface CraftArtifact {
  blob: Blob;
  name: string;
  size: number;
  optimization: string;
}
export type OnCraftAction = (artifacts: CraftArtifact[]) => void;

export function isValidMime(mimeType: string): mimeType is ImageCropperAllowedMimes {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(mimeType);
}

export function getExtensionFromUrl(url: string): string {
  const match = url.match(/\.([a-z0-9]+)(\?.*)?$/i);
  if (match && match[1]) {
    return `.${match[1].toLowerCase()}`;
  }
  return '';
}

export function getExtensionFromMime(mime: ImageCropperAllowedMimes): string {
  const map: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
  };

  return map[mime] || '';
}

export function getMimeFromExtension(ext: string): string {
  const map: Record<string, ImageCropperAllowedMimes> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
  };

  return map[ext.toLowerCase()] || '';
}

function getBlob(
  canvas: HTMLCanvasElement,
  options: {
    quality: number;
    mimeType?: string;
  }
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const type = options.mimeType ?? 'image/png';

    if (type === 'image/png') {
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Failed to get canvas context'));
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // options.quality it's a number between 0 (minimum) and 1 (maximum)
      // UPNG quality scale: 0 loseless, 256 lossy
      // We need to convert it to a number between 0 and 256
      const MAX_QUALITY = 256;
      const quality = Math.round((options.quality * 100 * MAX_QUALITY) / 100);

      const png = UPNG.encode([data.buffer], canvas.width, canvas.height, quality);
      const file = new Blob([png], { type });

      resolve(file);
    } else {
      canvas.toBlob(
        blob => {
          if (!blob) return reject(new Error('Failed to create blob'));
          resolve(blob);
        },
        type,
        options.quality
      );
    }
  });
}

export interface ImageCropperBundle {
  craftImage: (
    key: string | undefined,
    purpose: BubbleImageThumbnailPurpose,
    defaultOptimization?: string,
    onCropCb?: OnCraftAction
  ) => void;
}

const ImageCropperContext = React.createContext<ImageCropperBundle | undefined>(undefined);

export function useImageCropper() {
  const context = useContext(ImageCropperContext);

  if (context === undefined) {
    throw new Error('useImageProcessor must be used within a ImageProcessorProvider');
  }

  return context;
}

export const NO_IMAGE_KEY = '__new-image_';

export default function WithImageCropper(props: React.HTMLAttributes<HTMLDivElement>) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [specs, _setSpecs] = useState<ImageCropperSpecs>({ key: NO_IMAGE_KEY });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [onCraftCb, setOnCraftCb] = useState<OnCraftAction | undefined>(undefined);
  const [purpose, setPurpose] = useState<BubbleImageThumbnailPurpose>('main');
  const [isBusy, setIsBusy] = useState(false);

  const { plugins } = useServerInfo();
  const [requirement, setRequirement] = useState<ImageRequirementOption | undefined>(undefined);

  let requirementOpts = getImageRequirementOptions(plugins);
  if (!['first', 'main'].includes(purpose)) {
    requirementOpts = requirementOpts.filter(
      opt => ![ORIGINAL_OPTIMIZATION].includes(opt.optimization)
    );
  }

  const autoOptimizeFilterOpts = requirementOpts.filter(
    opt => ![ORIGINAL_OPTIMIZATION].includes(opt.optimization)
  );
  const [autoOptimize, setAutoOptimize] = useState(true);

  useEffect(() => {
    if (!showPopup) {
      reset();
    }
  }, [showPopup]);

  function setSpecs(newSpecs: ImageCropperSpecs) {
    const updatedSpecs = { ...specs, ...newSpecs };

    if (!updatedSpecs.key.startsWith(NO_IMAGE_KEY)) {
      const img = new window.Image();

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        _setSpecs({
          ...updatedSpecs,
          realWidth: width,
          realHeight: height,
        });
      };

      img.src = updatedSpecs.key;
    } else {
      _setSpecs(updatedSpecs);
    }
  }

  /**
   * Calculate initial crop area given image dimensions and aspect ratio.
   */
  function getInitialCroppedAreaPixels(
    imageWidth: number,
    imageHeight: number,
    aspect: number
  ): Area {
    const imageAspect = imageWidth / imageHeight;
    let cropWidth: number, cropHeight: number;

    if (imageAspect > aspect) {
      // Image is wider than desired aspect
      cropHeight = imageHeight;
      cropWidth = cropHeight * aspect;
    } else {
      // Image is taller than desired aspect
      cropWidth = imageWidth;
      cropHeight = cropWidth / aspect;
    }

    const x = (imageWidth - cropWidth) / 2;
    const y = (imageHeight - cropHeight) / 2;

    return {
      x,
      y,
      width: cropWidth,
      height: cropHeight,
    };
  }

  function reset() {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSpecs({ key: NO_IMAGE_KEY });
    setCroppedAreaPixels(null);
    setOnCraftCb(undefined);
    setPurpose('main');
  }

  function onSelectFile(file: File) {
    const mimeType = file.type;

    if (!isValidMime(mimeType)) {
      alert('Only JPEG, PNG, WEBP files are allowed.');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => setSpecs({ key: reader.result as string }));
    reader.readAsDataURL(file);
  }

  async function optimizeBlobHelper(
    canvas: HTMLCanvasElement,
    imageRequirement?: ImageRequirementOption
  ): Promise<Blob> {
    if (!requirement) {
      throw new Error('No image option selected for optimization');
    }

    let low = 0.1;
    let high = 1.0;
    let bestBlob: Blob | null = null;

    const precision = 0.1; // how close we want low and high

    const imgRequirement = imageRequirement ?? requirement;

    while (high - low > precision) {
      const mid = (low + high) / 2;
      const blob = await getBlob(canvas, { quality: mid });

      if (blob.size <= imgRequirement.maxBytes) {
        bestBlob = blob;
        low = mid; // keep searching higher
      } else {
        high = mid; // search lower
      }
    }

    const fullQualityBlob = await getBlob(canvas, {
      quality: 1.0,
    });
    if (fullQualityBlob.size <= imgRequirement.maxBytes) {
      bestBlob = fullQualityBlob;
    }

    if (!bestBlob) {
      alert(
        `Image is too large to compress under ${imgRequirement.maxBytes} bytes. Please try a smaller image.`
      );
      console.error('Image is too large to compress under size limit:', imgRequirement.maxBytes);
      throw new Error('Unable to create image under size limit');
    }

    return bestBlob;
  }

  async function createBlob(
    crop: Area,
    width: number,
    height: number,
    imageRequirement?: ImageRequirementOption
  ) {
    const image: HTMLImageElement = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = error => reject(error);
      img.src = specs.key;
    });

    // Don't upscale canvas with DPR — use final output size
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);

    return optimizeBlobHelper(canvas, imageRequirement);
  }

  const handleCrop = useCallback(async () => {
    const width = requirement?.width ?? specs.realWidth;
    const height = requirement?.height ?? specs.realHeight;

    if (
      !croppedAreaPixels ||
      !onCraftCb ||
      !requirement ||
      width === undefined ||
      height === undefined
    ) {
      throw new Error('Optimization, image cropped area, specs, or onCraft callback is not set');
    }

    try {
      setIsBusy(true);
      const artifacts: CraftArtifact[] = [];

      if (
        (requirement.width && croppedAreaPixels.width < requirement.width) ||
        (requirement.height && croppedAreaPixels.height < requirement.height)
      ) {
        const yes = await Alert.question(
          `Cropped area is smaller than the optimization requirements. Pixelation will occur! Are you sure you want to continue?`
        );

        if (!yes) return;
      }

      const croppedBlob = await createBlob(croppedAreaPixels, width, height);

      const name = new Date().toISOString().replace(/[:.]/g, '-'); // Use current date as name
      const size = croppedBlob.size;

      artifacts.push({ blob: croppedBlob, name, size, optimization: requirement.optimization });

      if (purpose === 'first' && autoOptimize) {
        for (const autoOpt of autoOptimizeFilterOpts) {
          if (!autoOpt.width || !autoOpt.height) return;
          const autoCroppedBlob = await createBlob(
            getInitialCroppedAreaPixels(width, height, autoOpt.width / autoOpt.height),
            autoOpt.width,
            autoOpt.height,
            autoOpt
          );

          artifacts.push({
            blob: autoCroppedBlob,
            name: `${name}-${autoOpt.optimization}`,
            size: autoCroppedBlob.size,
            optimization: autoOpt.optimization,
          });
        }
      }

      onCraftCb(artifacts);
      setShowPopup(false);
    } finally {
      setIsBusy(false);
    }
  }, [specs, croppedAreaPixels, onCraftCb, requirement]);

  const imageProcessorBundle: ImageCropperBundle = {
    craftImage(
      key: string | undefined,
      purpose: BubbleImageThumbnailPurpose,
      defaultOptimization?: string,
      onCropCb?: OnCraftAction
    ) {
      setSpecs({ key: key ?? NO_IMAGE_KEY });
      setPurpose(purpose);
      setRequirement(
        requirementOpts.find(o => o.optimization === defaultOptimization) ||
          (['first', 'main'].includes(purpose)
            ? requirementOpts.find(opt => opt.optimization === ORIGINAL_OPTIMIZATION)
            : undefined)
      );
      setOnCraftCb(() => onCropCb);
      setTimeout(() => {
        setShowPopup(true);
      }, 0); // Ensure the popup opens after the state is set
    },
  };

  return (
    <ImageProcessorProvider imageProcessorBundle={imageProcessorBundle}>
      {showPopup && (
        <Popup
          onClose={() => setShowPopup(false)}
          title="Upload Image"
          position="full"
          overlayStyle={{ zIndex: 52 }}
          wrapperStyle={{ zIndex: 53 }}
        >
          <BubbleAlert variant="ocean" className="m-4">
            <span className="font-semibold">Images are resized to meet requirements.</span> Upload a
            large image to avoid pixelation when cropping.
          </BubbleAlert>
          <div className="flex items-center space-x-2 px-3 py-2 w-full bg-eggshell border-t border-b border-eggshell-darkest">
            <BubbleSelect
              block
              allowUndefined
              value={requirement}
              onValue={setRequirement}
              labelExtractor={opt => opt.name}
              options={requirementOpts}
              disabled={purpose != 'variant'}
              undefinedLabel="Optimize for..."
            />
            {requirement && (
              <UploadButton
                singleUpload
                onFilesChange={f => onSelectFile(f[0])}
                // Render props FTW
                render={upload => {
                  return (
                    <BubbleButton variant="navy" onClick={upload} icon="file-upload">
                      Choose File
                    </BubbleButton>
                  );
                }}
              />
            )}
          </div>
          <div className="w-full flex space-x-4 p-4">
            <div className="flex flex-grow flex-col space-y-4">
              <div className="flex flex-col space-y-4">
                {requirement && (
                  <div>
                    <div className="text-slate font-semibold">Requirements:</div>
                    <div className="flex items-center space-x-2">
                      {requirement.aspect && (
                        <BubbleBadge>Aspect: {requirement.aspect}</BubbleBadge>
                      )}
                      {requirement.width && <BubbleBadge>Width: {requirement.width}</BubbleBadge>}
                      {requirement.height && (
                        <BubbleBadge>Height: {requirement.height}</BubbleBadge>
                      )}
                      <BubbleBadge>
                        Max size: <Bytes bytes={requirement.maxBytes} />
                      </BubbleBadge>
                    </div>
                  </div>
                )}
                {!specs.key.startsWith(NO_IMAGE_KEY) && (
                  <div>
                    <div className="text-slate font-semibold">Source Image:</div>
                    <div className="flex items-center space-x-2">
                      {specs.realWidth && (
                        <BubbleBadge
                          variant={
                            !requirement?.width
                              ? 'slate'
                              : specs.realWidth > requirement.width
                              ? 'lime'
                              : 'crimson'
                          }
                        >
                          Width: {specs.realWidth}
                        </BubbleBadge>
                      )}
                      {specs.realHeight && (
                        <BubbleBadge
                          variant={
                            !requirement?.height
                              ? 'slate'
                              : specs.realHeight > requirement.height
                              ? 'lime'
                              : 'crimson'
                          }
                        >
                          Height: {specs.realHeight}
                        </BubbleBadge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col space-y-4 w-100">
              {!specs.key.startsWith(NO_IMAGE_KEY) &&
                requirement?.optimization !== ORIGINAL_OPTIMIZATION && (
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon="magnifying-glass" />
                    <BubbleInput
                      className="flex-grow"
                      type="range"
                      value={zoom}
                      min={1}
                      max={5}
                      step={0.05}
                      onValue={setZoom}
                    />
                  </div>
                )}
              {!specs.key.startsWith(NO_IMAGE_KEY) && (
                <div className="relative w-full aspect-square border">
                  <Cropper
                    showGrid={requirement?.optimization != ORIGINAL_OPTIMIZATION}
                    image={specs.key}
                    crop={crop}
                    zoom={zoom}
                    aspect={
                      (requirement?.width ?? specs.realWidth ?? 1) /
                      (requirement?.height ?? specs.realHeight ?? 1)
                    }
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_data, crop) => setCroppedAreaPixels(crop)}
                  />
                </div>
              )}
            </div>
          </div>
          {!specs.key.startsWith(NO_IMAGE_KEY) && (
            <div className="p-4 sticky bottom-0 bg-white">
              {purpose === 'first' && (
                <div className="mb-3">
                  <div className="flex items-center relative">
                    <BubbleInput
                      type="checkbox"
                      checked={autoOptimize}
                      onValue={setAutoOptimize}
                      inputClassName="stretched-link"
                      className="mr-1"
                    />
                    <span>Auto Optimize</span>
                  </div>
                  <div className="text-smoke text-xs mt-2">
                    *The system will attempt to create multiple versions of the image with different
                    optimizations. Pixelation may occur. For more control, please add optimizations
                    yourself via the image details in the image library.
                  </div>
                </div>
              )}
              <BubbleOverlay loading={isBusy}>
                <BubbleButton block variant="navy" onClick={handleCrop} disabled={!requirement}>
                  Optimize & Upload
                </BubbleButton>
              </BubbleOverlay>
            </div>
          )}
        </Popup>
      )}
      {props.children}
    </ImageProcessorProvider>
  );
}

interface ImageProcessorProviderPropsBase {
  imageProcessorBundle: ImageCropperBundle;
}

type ImageProcessorProviderProps = ImageProcessorProviderPropsBase & { children?: React.ReactNode };

function ImageProcessorProvider(props: ImageProcessorProviderProps) {
  const { imageProcessorBundle, children } = props;

  return (
    <ImageCropperContext.Provider value={imageProcessorBundle}>
      {children}
    </ImageCropperContext.Provider>
  );
}
